import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createPlatformSubscriber,
  loadPlatformSubscribers,
  reissuePlatformSubscriberCode,
  type SubscriberLifecycleStatus,
  type SubscriberManagementRow,
} from "../platform/subscriberManagement";
import { loadPlatformProfile, updatePlatformSubscription } from "../platform/platformSubscriptions";
import { supabase } from "../auth/supabaseClient";

function statusLabel(status: SubscriberLifecycleStatus): string {
  if (status === "pending_entry") return "در انتظار ورود";
  if (status === "active") return "فعال";
  if (status === "suspended") return "تعلیق";
  if (status === "expired") return "منقضی";
  return "لغوشده";
}

function jalaliDate(value?: string): string {
  if (!value) return "—";
  return new Date(`${value}T12:00:00Z`).toLocaleDateString("fa-IR-u-ca-persian");
}

export function SubscriberManagementPanel() {
  const [checked, setChecked] = useState(false);
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const [rows, setRows] = useState<SubscriberManagementRow[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [managerName, setManagerName] = useState("");
  const [farmName, setFarmName] = useState("");
  const [days, setDays] = useState(30);
  const [actionDays, setActionDays] = useState(30);
  const [latestCode, setLatestCode] = useState<{ farmName: string; code: string }>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  const refresh = useCallback(async () => {
    setError(undefined);
    try {
      const profile = await loadPlatformProfile(supabase);
      setIsPlatformOwner(profile.isPlatformOwner);
      if (!profile.isPlatformOwner) {
        setRows([]);
        return;
      }

      const next = await loadPlatformSubscribers(supabase);
      setRows(next);
      setSelectedId((current) =>
        next.some((row) => row.provisionId === current) ? current : (next[0]?.provisionId ?? ""),
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "بارگذاری مشترکین انجام نشد.");
    } finally {
      setChecked(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selected = useMemo(
    () => rows.find((row) => row.provisionId === selectedId),
    [rows, selectedId],
  );

  const stats = useMemo(
    () => ({
      total: rows.length,
      active: rows.filter((row) => row.status === "active").length,
      pending: rows.filter((row) => row.status === "pending_entry").length,
      inactive: rows.filter(
        (row) =>
          row.status === "suspended" || row.status === "expired" || row.status === "cancelled",
      ).length,
    }),
    [rows],
  );

  async function run(action: () => Promise<void>, successMessage: string) {
    try {
      setBusy(true);
      setError(undefined);
      setMessage(undefined);
      await action();
      setMessage(successMessage);
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "عملیات مشترک انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  async function createSubscriber() {
    const normalizedManager = managerName.trim();
    const normalizedFarm = farmName.trim();
    if (!normalizedManager || !normalizedFarm || days < 1) {
      setError("نام مدیر، نام سالن و مدت اشتراک را کامل کنید.");
      return;
    }

    await run(async () => {
      const result = await createPlatformSubscriber(
        supabase,
        normalizedManager,
        normalizedFarm,
        days,
      );
      setLatestCode({ farmName: result.farmName ?? normalizedFarm, code: result.setupCode });
      setManagerName("");
      setFarmName("");
    }, "مشترک ایجاد شد و سالن خالی آماده است.");
  }

  async function reissueCode(row: SubscriberManagementRow) {
    await run(async () => {
      const result = await reissuePlatformSubscriberCode(supabase, row.provisionId);
      setLatestCode({ farmName: row.farmName, code: result.setupCode });
    }, "کد راه‌اندازی جدید صادر شد و کد قبلی دیگر معتبر نیست.");
  }

  if (!checked) {
    return (
      <section className="section-card">
        <p>در حال بارگذاری مدیریت مشترکین…</p>
      </section>
    );
  }

  if (!isPlatformOwner) return null;

  return (
    <>
      <section className="section-card" aria-labelledby="subscriber-management-title">
        <div className="section-title-row">
          <div>
            <p className="section-kicker">مالک سامانه</p>
            <h2 id="subscriber-management-title">مدیریت مشترکین</h2>
            <p className="section-note">
              هر مشترک یک سالن خالی دریافت می‌کند و مدیر سالن با کد راه‌اندازی حساب خود را متصل می‌کند.
            </p>
          </div>
          <button
            type="button"
            className="text-button"
            disabled={busy}
            onClick={() => void refresh()}
          >
            بروزرسانی
          </button>
        </div>

        <div className="quick-grid">
          <article className="manager-card">
            <p className="section-kicker">کل مشترک</p>
            <h2>{stats.total}</h2>
          </article>
          <article className="manager-card">
            <p className="section-kicker">فعال</p>
            <h2>{stats.active}</h2>
          </article>
          <article className="manager-card">
            <p className="section-kicker">در انتظار ورود</p>
            <h2>{stats.pending}</h2>
          </article>
          <article className="manager-card">
            <p className="section-kicker">غیرفعال</p>
            <h2>{stats.inactive}</h2>
          </article>
        </div>
      </section>

      <section className="section-card">
        <div className="section-title-row">
          <div>
            <p className="section-kicker">مشترک جدید</p>
            <h2>ایجاد مشترک جدید</h2>
            <p className="section-note">
              سالن بدون تانک ساخته می‌شود؛ ساختار داخلی سالن پس از اولین ورود مدیر تنظیم خواهد شد.
            </p>
          </div>
        </div>

        <div className="operation-field-grid">
          <label>
            نام مدیر مشترک
            <input
              value={managerName}
              placeholder="نام و نام خانوادگی"
              onChange={(event) => setManagerName(event.target.value)}
            />
          </label>
          <label>
            نام سالن
            <input
              value={farmName}
              placeholder="نام مجموعه مشترک"
              onChange={(event) => setFarmName(event.target.value)}
            />
          </label>
          <label>
            مدت اشتراک (روز)
            <input
              type="number"
              min="1"
              max="730"
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
            />
          </label>
        </div>

        <button
          type="button"
          className="primary-button large-button"
          disabled={busy}
          onClick={() => void createSubscriber()}
        >
          ایجاد مشترک و صدور کد
        </button>

        {latestCode ? (
          <article className="manager-card" style={{ marginTop: "1rem" }}>
            <p className="section-kicker">کد راه‌اندازی — فقط برای مدیر سالن</p>
            <h3>{latestCode.farmName}</h3>
            <code
              dir="ltr"
              style={{
                display: "block",
                fontSize: "1.2rem",
                fontWeight: 900,
                letterSpacing: "0.08em",
                padding: "0.8rem",
              }}
            >
              {latestCode.code}
            </code>
            <button
              type="button"
              className="text-button"
              onClick={() => {
                void navigator.clipboard?.writeText(latestCode.code);
              }}
            >
              کپی کد
            </button>
          </article>
        ) : null}

        {message ? <p className="operation-success">{message}</p> : null}
        {error ? <p className="settings-warning">{error}</p> : null}
      </section>

      <section className="section-card">
        <div className="section-title-row">
          <div>
            <p className="section-kicker">فهرست</p>
            <h2>فهرست مشترکین</h2>
          </div>
          <span className="count-pill">{rows.length}</span>
        </div>

        <div className="work-list">
          {rows.length === 0 ? (
            <p className="empty-state">هنوز مشترکی ایجاد نشده است.</p>
          ) : (
            rows.map((row) => (
              <button
                type="button"
                className="work-item pending"
                key={row.provisionId}
                onClick={() => setSelectedId(row.provisionId)}
              >
                <span className="work-main">
                  <strong>{row.farmName}</strong>
                  <span>
                    {row.managerName} · {statusLabel(row.status)}
                    {row.claimedEmail ? ` · ${row.claimedEmail}` : ""}
                  </span>
                </span>
                <span className="work-status">
                  {row.status === "pending_entry"
                    ? `کد ••••${row.setupCodeLast4}`
                    : row.daysRemaining === undefined
                      ? "—"
                      : `${row.daysRemaining} روز`}
                </span>
              </button>
            ))
          )}
        </div>

        {selected ? (
          <article className="manager-card" style={{ marginTop: "1rem" }}>
            <div className="section-title-row">
              <div>
                <p className="section-kicker">{statusLabel(selected.status)}</p>
                <h3>{selected.farmName}</h3>
              </div>
              <span className="count-pill">{selected.subscriptionDays} روز</span>
            </div>

            <p className="section-note">
              مدیر: {selected.managerName} · شروع: {jalaliDate(selected.startsOn)} · پایان:{" "}
              {jalaliDate(selected.expiresOn)}
            </p>

            {selected.status === "pending_entry" ? (
              <button
                type="button"
                className="secondary-button"
                disabled={busy}
                onClick={() => void reissueCode(selected)}
              >
                صدور کد جدید
              </button>
            ) : null}

            {selected.subscriptionId ? (
              <>
                <div className="operation-field-grid" style={{ marginTop: "0.8rem" }}>
                  <label>
                    مدت تمدید (روز)
                    <input
                      type="number"
                      min="1"
                      max="730"
                      value={actionDays}
                      onChange={(event) => setActionDays(Number(event.target.value))}
                    />
                  </label>
                </div>
                <div className="confirm-actions">
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={busy}
                    onClick={() =>
                      void run(
                        () =>
                          updatePlatformSubscription(
                            supabase,
                            selected.subscriptionId as string,
                            "renew",
                            actionDays,
                          ),
                        "اشتراک تمدید و فعال شد.",
                      )
                    }
                  >
                    تمدید
                  </button>

                  {selected.status === "suspended" ? (
                    <button
                      type="button"
                      className="text-button"
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () =>
                            updatePlatformSubscription(
                              supabase,
                              selected.subscriptionId as string,
                              "activate",
                            ),
                          "اشتراک فعال شد.",
                        )
                      }
                    >
                      فعال‌سازی
                    </button>
                  ) : selected.status === "active" ? (
                    <button
                      type="button"
                      className="text-button"
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () =>
                            updatePlatformSubscription(
                              supabase,
                              selected.subscriptionId as string,
                              "suspend",
                            ),
                          "اشتراک تعلیق شد.",
                        )
                      }
                    >
                      تعلیق
                    </button>
                  ) : null}
                </div>
              </>
            ) : null}
          </article>
        ) : null}
      </section>
    </>
  );
}
